import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { movimentacaoService } from "../../services/movimentacaoService";
import {
  MovimentacaoEmpreendimentoCreate,
  MovimentacaoUnidadeCreate,
  CamposMovimentacaoEmpreendimento,
  CamposMovimentacaoUnidade,
  UnifiedMovimentacaoCreate,
} from "../../types/movimentacao"; // Import new types

// Placeholder for select options (will need to fetch these from API or define globally)
const EMPREENDIMENTO_OPTIONS = [
  { value: 1, label: "Empreendimento A" },
  { value: 2, label: "Empreendimento B" },
];

const UNIDADE_OPTIONS = [
  { value: 1, label: "Unidade 101" },
  { value: 2, label: "Unidade 102" },
];

const CAMPO_ALTERADO_EMPREENDIMENTO_OPTIONS = Object.entries(CamposMovimentacaoEmpreendimento)
  .filter(([key, value]) => typeof value === 'number')
  .map(([key, value]) => ({ value: value as CamposMovimentacaoEmpreendimento, label: key.replace(/_/g, ' ') }));

const CAMPO_ALTERADO_UNIDADE_OPTIONS = Object.entries(CamposMovimentacaoUnidade)
  .filter(([key, value]) => typeof value === 'number')
  .map(([key, value]) => ({ value: value as CamposMovimentacaoUnidade, label: key.replace(/_/g, ' ') }));


interface EmpreendimentoMovementFormState extends MovimentacaoEmpreendimentoCreate {
  files: File[];
}

interface UnidadeMovementFormState extends MovimentacaoUnidadeCreate {
  files: File[];
}

const NovaMovimentacao: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [empreendimentoMovements, setEmpreendimentoMovements] = useState<EmpreendimentoMovementFormState[]>([]);
  const [unidadeMovements, setUnidadeMovements] = useState<UnidadeMovementFormState[]>([]);

  const handleAddEmpreendimentoMovement = useCallback(() => {
    setEmpreendimentoMovements(prev => [
      ...prev,
      {
        empreendimento: 0, // Placeholder, actual ID will come from selection
        campo_alterado: CamposMovimentacaoEmpreendimento.AREA_CONSTRUIDA, // Default or placeholder
        valor_antigo: "",
        valor_novo: "",
        files: [],
      },
    ]);
  }, []);

  const handleAddUnidadeMovement = useCallback(() => {
    setUnidadeMovements(prev => [
      ...prev,
      {
        unidade: 0, // Placeholder
        campo_alterado: CamposMovimentacaoUnidade.DISPONIBILIDADE, // Default or placeholder
        valor_antigo: "",
        valor_novo: "",
        files: [],
      },
    ]);
  }, []);

  const handleEmpreendimentoMovementChange = useCallback(
    (index: number, field: keyof MovimentacaoEmpreendimentoCreate, value: any) => {
      setEmpreendimentoMovements(prev =>
        prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
      );
    },
    []
  );

  const handleUnidadeMovementChange = useCallback(
    (index: number, field: keyof MovimentacaoUnidadeCreate, value: any) => {
      setUnidadeMovements(prev =>
        prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
      );
    },
    []
  );

  const handleEmpreendimentoFileChange = useCallback(
    (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      setEmpreendimentoMovements(prev =>
        prev.map((item, i) => (i === index ? { ...item, files: files } : item))
      );
    },
    []
  );

  const handleUnidadeFileChange = useCallback(
    (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      setUnidadeMovements(prev =>
        prev.map((item, i) => (i === index ? { ...item, files: files } : item))
      );
    },
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const unifiedData: UnifiedMovimentacaoCreate = {
        movimentacoes_empreendimento: empreendimentoMovements.map(({ files, ...rest }) => rest),
        movimentacoes_unidade: unidadeMovements.map(({ files, ...rest }) => rest),
        empreendimento_files: empreendimentoMovements.flatMap(m => m.files),
        unidade_files: unidadeMovements.flatMap(m => m.files),
      };

      await movimentacaoService.createUnifiedMovimentacao(unifiedData);
      navigate("/movimentacoes");
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao criar movimentação unificada");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Nova Movimentação Unificada</h1>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <strong className="font-bold">Erro:</strong>
            <span className="block sm:inline"> {error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
          {/* Empreendimento Movements Section */}
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Movimentações de Empreendimento</h2>
          {empreendimentoMovements.map((movement, index) => (
            <div key={`empreendimento-${index}`} className="border p-4 mb-4 rounded-md">
              <h3 className="text-xl font-medium mb-2">Empreendimento #{index + 1}</h3>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Empreendimento
                </label>
                <select
                  value={movement.empreendimento}
                  onChange={e =>
                    handleEmpreendimentoMovementChange(index, "empreendimento", parseInt(e.target.value))
                  }
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                >
                  <option value={0}>Selecione um empreendimento</option>
                  {EMPREENDIMENTO_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Campo Alterado
                </label>
                <select
                  value={movement.campo_alterado}
                  onChange={e =>
                    handleEmpreendimentoMovementChange(index, "campo_alterado", parseInt(e.target.value))
                  }
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                >
                  {CAMPO_ALTERADO_EMPREENDIMENTO_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Valor Antigo
                </label>
                <input
                  type="text"
                  value={movement.valor_antigo}
                  onChange={e => handleEmpreendimentoMovementChange(index, "valor_antigo", e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Valor Novo
                </label>
                <input
                  type="text"
                  value={movement.valor_novo}
                  onChange={e => handleEmpreendimentoMovementChange(index, "valor_novo", e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Anexar Arquivos
                </label>
                <input
                  type="file"
                  multiple
                  onChange={e => handleEmpreendimentoFileChange(index, e)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddEmpreendimentoMovement}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline mb-6"
          >
            Adicionar Movimentação de Empreendimento
          </button>

          {/* Unidade Movements Section */}
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">Movimentações de Unidade</h2>
          {unidadeMovements.map((movement, index) => (
            <div key={`unidade-${index}`} className="border p-4 mb-4 rounded-md">
              <h3 className="text-xl font-medium mb-2">Unidade #{index + 1}</h3>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Unidade
                </label>
                <select
                  value={movement.unidade}
                  onChange={e =>
                    handleUnidadeMovementChange(index, "unidade", parseInt(e.target.value))
                  }
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                >
                  <option value={0}>Selecione uma unidade</option>
                  {UNIDADE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Campo Alterado
                </label>
                <select
                  value={movement.campo_alterado}
                  onChange={e =>
                    handleUnidadeMovementChange(index, "campo_alterado", parseInt(e.target.value))
                  }
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                >
                  {CAMPO_ALTERADO_UNIDADE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Valor Antigo
                </label>
                <input
                  type="text"
                  value={movement.valor_antigo}
                  onChange={e => handleUnidadeMovementChange(index, "valor_antigo", e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Valor Novo
                </label>
                <input
                  type="text"
                  value={movement.valor_novo}
                  onChange={e => handleUnidadeMovementChange(index, "valor_novo", e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Anexar Arquivos
                </label>
                <input
                  type="file"
                  multiple
                  onChange={e => handleUnidadeFileChange(index, e)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddUnidadeMovement}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline mb-6"
          >
            Adicionar Movimentação de Unidade
          </button>

          <div className="flex items-center justify-between mt-8">
            <button
              type="button"
              onClick={() => navigate("/movimentacoes")}
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50"
              disabled={loading}
            >
              {loading ? "Salvando..." : "Salvar Movimentações Unificadas"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NovaMovimentacao; 